// components/support/SupportModal.jsx
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CatLogo } from '@/components/CatLogo';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { app } from '@/firebase/config';
import { authManager } from '@/managers/AuthManager';
import { FaReddit } from 'react-icons/fa';
import ReactDOM from 'react-dom';

const firestore = getFirestore(app);

const SupportModal = ({ isOpen, onClose }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const currentUser = authManager.getCurrentUser();
    
    if (!currentUser) {
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { 
          message: 'Please sign in to contact support',
          type: 'error' 
        }
      }));
      return;
    }

    if (!message.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      const userTier = authManager.getTier();
      
      await addDoc(collection(firestore, 'support-messages'), {
        message: message.trim(),
        userId: currentUser.uid,
        email: currentUser.email,
        timestamp: serverTimestamp(),
        metadata: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          pathname: window.location.pathname,
          tier: userTier,
          displayName: currentUser.displayName || 'Anonymous User'
        },
        status: 'unread'
      });
      
      setMessage('');
      onClose();
      
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { 
          message: 'Message sent! We\'ll respond via email shortly.',
          type: 'success' 
        }
      }));
      
    } catch (error) {
      console.error('Error submitting message:', error);
      
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { 
          message: 'Failed to send message. Please try again.',
          type: 'error' 
        }
      }));
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentUser = authManager.getCurrentUser();
  const isAuthenticated = !!currentUser;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 pointer-events-none z-[5000]">
      <div className="relative pointer-events-auto z-[5400]">
        <div className="fixed inset-0 flex items-end justify-end p-6 max-[840px]:items-center max-[840px]:justify-center max-[840px]:p-4">
          <div className="absolute inset-0 bg-black/30" onClick={onClose} />
          <div className="relative bg-surface border border-border-color rounded-xl shadow-glass w-full max-w-sm animate-slideFromTop">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border-color">
              <div className="flex items-center gap-2">
                <CatLogo className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-on-surface">Support</span>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href="https://reddit.com/r/Shelbula"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[11px] text-on-surface-dim hover:text-primary transition-colors"
                >
                  <FaReddit size={12} />
                  r/Shelbula
                </a>
                <button onClick={onClose} className="text-on-surface-dim hover:text-on-surface transition-colors">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-4">
              {isAuthenticated ? (
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="text-[11px] text-on-surface-dim">
                    Sending as <span className="text-on-surface">{currentUser.email}</span>
                  </div>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full h-32 px-3 py-2 bg-background-dark/60 rounded-md border border-border-color
                               text-sm text-on-surface placeholder:text-on-surface-dim resize-none
                               focus:outline-none focus:border-primary/40 transition-colors"
                    placeholder="What do you need help with?"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting || !message.trim()}
                    className="w-full px-4 py-2 bg-primary hover:bg-primary-dark text-white text-sm
                               rounded-md transition-colors font-medium
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending…' : 'Send Message'}
                  </button>
                  <p className="text-[11px] text-on-surface-dim text-center">
                    Or email us at{' '}
                    <a href="mailto:support@shelbula.com" className="text-primary hover:underline">
                      support@shelbula.com
                    </a>
                  </p>
                </form>
              ) : (
                <div className="flex flex-col items-center gap-3 py-4">
                  <p className="text-sm text-on-surface-dim text-center">Sign in to contact support.</p>
                  <button
                    onClick={() => window.dispatchEvent(new Event('open-auth-modal'))}
                    className="px-4 py-2 bg-primary hover:bg-primary-dark text-white text-sm rounded-md transition-colors"
                  >
                    Sign In
                  </button>
                  <p className="text-[11px] text-on-surface-dim text-center">
                    Or email{' '}
                    <a href="mailto:support@shelbula.com" className="text-primary hover:underline">
                      support@shelbula.com
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('portal-root')
  );
};

export default SupportModal;